import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronRight, Home, ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/cn'

interface Crumb {
  label: string
  to?: string
}

interface Props {
  title: string
  subtitle?: string
  crumbs?: Crumb[]
  actions?: ReactNode
  className?: string
  /**
   * Botão "Voltar" antes do título.
   *   - `true` → volta no histórico (`navigate(-1)`)
   *   - string → rota para onde navegar (`<Link to>`)
   */
  backTo?: true | string
}

/**
 * Cabeçalho padrão da página.
 *
 * Estrutura:
 *
 *   ┌ breadcrumbs (linha separada, sem influenciar alinhamentos) ┐
 *   │  Home > Configurações >  > Item                            │
 *   └─────────────────────────────────────────────────────────────┘
 *   ┌─ alinhamento vertical-center ───────────────────────────────┐
 *   │  [← Voltar]  Título                            [ Salvar ]   │
 *   │              Subtítulo                                       │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Os breadcrumbs ficam ACIMA do bloco principal e NÃO influenciam o
 * alinhamento vertical entre `backTo`, título/subtítulo e ações — esses 3
 * elementos ficam sempre centralizados entre si (vertical-center sobre a
 * altura do conjunto título+subtítulo).
 */
export function PageHeader({ title, subtitle, crumbs, actions, className, backTo }: Props) {
  const navigate = useNavigate()

  return (
    <header className={cn('mb-6', className)}>
      {/* Breadcrumbs (linha separada) */}
      {crumbs && crumbs.length > 0 && (
        <nav className="flex items-center text-[11px] text-gray-500 gap-1 mb-3">
          <Link to="/" className="cursor-pointer hover:text-gray-700 flex items-center gap-1">
            <Home size={11} />
          </Link>
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1">
              <ChevronRight size={11} className="text-gray-300" />
              {c.to
                ? <Link to={c.to} className="cursor-pointer hover:text-gray-700">{c.label}</Link>
                : <span className="text-gray-700">{c.label}</span>}
            </span>
          ))}
        </nav>
      )}

      {/* Bloco principal — alinhamento vertical-center entre backTo, título e actions */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {backTo && (
            backTo === true ? (
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Voltar"
                className="cursor-pointer inline-flex items-center justify-center w-10 h-10 rounded-md bg-white border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition shrink-0"
              >
                <ArrowLeft size={16} />
              </button>
            ) : (
              <Link
                to={backTo}
                aria-label="Voltar"
                className="cursor-pointer inline-flex items-center justify-center w-10 h-10 rounded-md bg-white border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition shrink-0"
              >
                <ArrowLeft size={16} />
              </Link>
            )
          )}
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-800 break-words">{title}</h1>
            {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
    </header>
  )
}
